import * as storage from '../utils/LocalStorageWrapper.js';
import {normalizeLayouts, controlGeometry, placeControl} from './mobileLayout.js';

const PREFIX = 'jollyworld-mobile-level-controls-v1:';
const directionPairs = [[65, 37], [68, 39], [87, 38], [83, 40]];
export const levelKeyId = code => `key:${code}`;
const validCode = code => Number.isInteger(code) && code > 0 && code < 256;
const keyIds = Array.from({length: 255}, (_, i) => levelKeyId(i + 1));
export const normalizeLevelLayouts = value => normalizeLayouts(value, keyIds);
export const levelButtonBase = {left: 0, top: 0, width: 52, height: 52};

export function placeLevelKey(viewport, occupied) {
    const inset = viewport.insets || {left: 8, right: 8, top: 8, bottom: 8};
    const startY = Math.max(inset.top + 88, viewport.height - 310);
    for (let y = startY; y >= inset.top + 88; y -= 64) {
        for (let x = viewport.width - inset.right - 60; x >= inset.left; x -= 64) {
            const saved = placeControl(levelButtonBase, {x: x + 26, y: y + 26}, 1, viewport);
            const box = controlGeometry(levelButtonBase, saved, viewport);
            if (occupied.every(other => box.left + box.width + 8 <= other.left || other.left + other.width + 8 <= box.left ||
                box.top + box.height + 8 <= other.top || other.top + other.height + 8 <= box.top)) return saved;
        }
    }
    return placeControl(levelButtonBase, {x: viewport.width / 2, y: startY + 26}, 1, viewport);
}

export function discoverLevelKeys(triggers = []) {
    // Disabled triggers may be enabled later in the level. Include both press
    // and release triggers, deduplicating repeated uses of the same key.
    return [...new Set(triggers.map(body => body.mySprite?.data || body.data || body)
        .filter(data => [6, 7].includes(data.targetType) && validCode(data.triggerKey))
        .map(data => data.triggerKey))].sort((a, b) => a - b);
}

export function mobileDirectionKeys(code, required = []) {
    const pair = directionPairs.find(pair => pair[0] === code);
    return pair && !pair.every(key => required.includes(key)) ? pair : [code];
}

export function extraLevelKeys(required) {
    return required.filter(code => {
        const pair = directionPairs.find(pair => pair.includes(code));
        // If both keys are authored, expose exact buttons for each. Do not
        // assume that e.g. A and Left trigger the same thing in that level.
        if (pair) return pair.every(key => required.includes(key));
        // Eject, Grab and Hat can disappear with the character's current mode.
        // Offer their exact keys too, along with keys reserved by game menus.
        return code !== 32;
    });
}

export function mobileLevelId(data) {
    if (!data) return null;
    if (data.id) return `id:${data.id}`;
    if (data.level_md5) return `md5:${data.level_md5}`;
    const json = typeof data.json === 'string' ? data.json : JSON.stringify(data.json);
    if (!json) return null;
    let hash = 2166136261;
    for (let i = 0; i < json.length; i++) hash = Math.imul(hash ^ json.charCodeAt(i), 16777619);
    return `local:${(hash >>> 0).toString(16)}:${json.length}`;
}

export const mobileLevelControls = {
    id: null, required: [], keys: [], layouts: normalizeLevelLayouts(),
    configure(data, triggers) {
        this.id = mobileLevelId(data);
        this.required = discoverLevelKeys(triggers);
        this.keys = extraLevelKeys(this.required);
        let saved;
        try { if (this.id) saved = JSON.parse(storage.getItem(PREFIX + encodeURIComponent(this.id))); } catch { /* Defaults. */ }
        this.layouts = normalizeLevelLayouts(saved);
    },
    save(layouts) {
        this.layouts = normalizeLevelLayouts(layouts);
        if (this.id) storage.setItem(PREFIX + encodeURIComponent(this.id), JSON.stringify(this.layouts));
    },
};

// One touch button can generate several compatible keys. Keep ownership so a
// second button cannot release a shared key, and remember keys across flips.
export class MobileKeyHolds {
    constructor(emit) { this.emit = emit; this.owners = new Map(); }
    has(owner) { return this.owners.has(owner); }
    press(owner, keys) {
        if (this.has(owner)) return;
        const held = new Set([...this.owners.values()].flat());
        this.owners.set(owner, [...new Set(keys)]);
        for (const key of this.owners.get(owner)) if (!held.has(key)) this.emit(true, key);
    }
    release(owner) {
        const keys = this.owners.get(owner) || [];
        this.owners.delete(owner);
        const held = new Set([...this.owners.values()].flat());
        for (const key of keys) if (!held.has(key)) this.emit(false, key);
    }
    reset() { for (const owner of [...this.owners.keys()]) this.release(owner); }
}
